import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminApi = axios.create({ baseURL: `${API}/admin/media`, withCredentials: true });

export const listPublicMedia = async () => (await axios.get(`${API}/media`)).data;
export const listAdminMedia = async () => (await adminApi.get("")).data;
export const createMediaItem = async (payload) => (await adminApi.post("", payload)).data;
export const updateMediaItem = async (id, payload) => (await adminApi.patch(`/${id}`, payload)).data;
export const deleteMediaItem = async (id) => (await adminApi.delete(`/${id}`)).data;

const putBlob = (url, blob, contentType, onProgress, signal) => new Promise((resolve, reject) => {
  const request = new XMLHttpRequest();
  request.open("PUT", url);
  if (contentType) request.setRequestHeader("Content-Type", contentType);
  request.upload.onprogress = (event) => {
    if (event.lengthComputable) onProgress((event.loaded / event.total) * 100);
  };
  request.onload = () => {
    if (request.status >= 200 && request.status < 300) resolve(request.getResponseHeader("ETag"));
    else reject(new Error("Cloud storage rejected this upload."));
  };
  request.onerror = () => reject(new Error("The upload connection was interrupted."));
  request.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
  const abort = () => request.abort();
  signal?.addEventListener("abort", abort, { once: true });
  request.onloadend = () => signal?.removeEventListener("abort", abort);
  request.send(blob);
});

export const uploadMediaAsset = async ({ file, kind, onProgress, signal }) => {
  let initiated;
  try {
    initiated = (await adminApi.post("/uploads/initiate", {
      kind,
      filename: file.name,
      content_type: file.type,
      size: file.size,
    })).data;

    const parts = [];
    if (initiated.strategy === "multipart") {
      const totalParts = Math.ceil(file.size / initiated.part_size);
      for (let index = 0; index < totalParts; index += 1) {
        if (signal?.aborted) throw new DOMException("Upload cancelled", "AbortError");
        const partNumber = index + 1;
        const signed = (await adminApi.post("/uploads/sign-part", {
          upload_id: initiated.upload_id,
          part_number: partNumber,
        })).data;
        const start = index * initiated.part_size;
        const part = file.slice(start, Math.min(start + initiated.part_size, file.size));
        const etag = await putBlob(
          signed.url,
          part,
          file.type,
          (value) => onProgress(((index + value / 100) / totalParts) * 100),
          signal,
        );
        if (!etag) throw new Error("The storage response did not include an ETag. Check the R2 CORS policy.");
        parts.push({ part_number: partNumber, etag });
      }
    } else {
      await putBlob(initiated.upload_url, file, file.type, onProgress, signal);
    }

    return (await adminApi.post("/uploads/complete", {
      upload_id: initiated.upload_id,
      parts,
    })).data;
  } catch (error) {
    if (initiated?.upload_id) {
      try { await adminApi.post(`/uploads/${initiated.upload_id}/abort`); } catch { /* best-effort cleanup */ }
    }
    throw error;
  }
};

export const mediaApiError = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  return typeof detail === "string" ? detail : error.message || fallback;
};